import { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel } from '../../lib/format';
import { Pill, Spinner, EmptyState, SearchField, ErrorNotice } from '../../components/ui';
import { ReworkBatchEditor } from './ReworkBatchEditor';
import { ReworkBatchDetailPanel } from './ReworkBatchDetailPanel';

// 客户售后返工 · 返修批次列表（阶段 1：批次登记与来源追溯）
// 设计文档 docs/aftersales-rework-batch-redesign-2026-10-16.md v3 §5.2
// 每批一行；状态与交货状态分列（历史单件「返工完成≠已交货」单独标注）。

type BatchRow = {
  id: string;
  number: string;
  customer_id?: string;
  customer_repair_number?: string | null;
  receipt_date?: string;
  reason?: string;
  status?: string;
  origin?: string;
  item_count?: number;
  in_progress_count?: number;
  qualified_count?: number;
  delivered_count?: number;
  delivery_state?: string;
  created_at?: string;
  customer_code?: string | null;
  customer_name?: string | null;
};
type ListResp = { rows: BatchRow[]; total: number; limit: number; offset: number };

const STATUS: Record<string, { label: string; tone: 'ok' | 'info' | 'warn' | 'neutral' | 'bad' }> = {
  draft: { label: '草稿', tone: 'neutral' },
  received: { label: '已收件', tone: 'info' },
  in_progress: { label: '返工中', tone: 'info' },
  pending_inspection: { label: '待检验', tone: 'warn' },
  pending_delivery: { label: '待交货', tone: 'warn' },
  partially_delivered: { label: '部分交货', tone: 'ok' },
  completed: { label: '已完成', tone: 'ok' },
  cancelled: { label: '已取消', tone: 'neutral' },
};
const DELIVERY_STATE: Record<string, { label: string; tone: 'ok' | 'info' | 'warn' | 'neutral' }> = {
  none: { label: '未交货', tone: 'neutral' },
  partial: { label: '部分交货', tone: 'info' },
  complete: { label: '已交货', tone: 'ok' },
  unknown: { label: '返工完成·交货待核', tone: 'warn' },
};

// 返修不含费用；如产生增项，另开销售合同（列表不再展示费用列）。
export function ReworkBatchListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = can(user, 'rework-batches', 'create');
  const [creating, setCreating] = useState(false);
  const [rows, setRows] = useState<BatchRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [page, setPage] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const limit = 50;

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(page * limit) });
      if (debouncedQ) params.set('q', debouncedQ);
      const data = await api.detail<ListResp>(`/api/rework-batches?${params.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e: any) {
      setError(e?.message || '数据加载失败');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedQ]);
  useEffect(() => { void load(); }, [load]);

  const hasMore = page * limit + rows.length < total;

  return (
    <>
      {creating ? (
        <ReworkBatchEditor
          onDone={() => { setCreating(false); setPage(0); void load(); }}
          onCancel={() => setCreating(false)}
        />
      ) : null}

      <section className="panel">
        <div className="panel-head">
          <div style={{ minWidth: 0 }}>
            <h2>售后返工 · 返修批次</h2>
            <div className="desc">
              客户返修批次（一张单含多件工件）：收件 → 来源追溯 → 返工 → 检验 → 返工交货。原交货/原合同/原检验只读保留。
              返修不含费用，如产生增项请另开销售合同。
            </div>
          </div>
          <div className="spacer" />
          {canWrite ? (
            <button className="btn primary" onClick={() => setCreating((v) => !v)}>
              {creating ? '收起新建' : '新建返修批次'}
            </button>
          ) : null}
        </div>

        <div className="toolbar" style={{ alignItems: 'flex-end' }}>
          <div className="field" style={{ flex: 1, minWidth: 240 }}>
            <label>关键字搜索</label>
            <SearchField value={q} onChange={setQ} placeholder="系统/客户返修单号、客户名称…" />
          </div>
          <div className="field">
            <label>操作</label>
            <button className="btn" onClick={() => void load()} disabled={loading}>刷新</button>
          </div>
        </div>

        {error ? (
          <div style={{ padding: 'var(--pad)' }}><ErrorNotice message={error} onRetry={() => void load()} /></div>
        ) : (
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>系统返修单号</th>
                  <th>客户</th>
                  <th>客户返修单号</th>
                  <th style={{ textAlign: 'right' }}>收件</th>
                  <th style={{ textAlign: 'right' }}>返工中</th>
                  <th style={{ textAlign: 'right' }}>合格待交</th>
                  <th style={{ textAlign: 'right' }}>已交</th>
                  <th>状态</th>
                  <th>交货状态</th>
                  <th>到货日期</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr><td colSpan={10} style={{ padding: 'var(--pad)' }}><Spinner label="正在从核心服务读取数据…" /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={10} style={{ padding: 0 }}><EmptyState>没有符合条件的记录。</EmptyState></td></tr>
                ) : rows.map((r) => {
                  const legacy = r.origin === 'legacy';
                  const open = openId === r.id && !legacy;
                  const s = STATUS[r.status || 'draft'] || STATUS.draft;
                  const d = DELIVERY_STATE[r.delivery_state || 'none'] || DELIVERY_STATE.none;
                  return (
                    <Fragment key={r.id}>
                      <tr
                        className={open ? 'sel-row' : undefined}
                        onClick={() => { if (legacy) { navigate('/after-sales'); return; } setOpenId(open ? null : r.id); }}
                        aria-expanded={open}
                        title={legacy ? '历史单件返工：点击前往原售后页查看' : undefined}
                      >
                        <td>
                          <span className="num strong">{r.number || r.id}</span>
                          {legacy ? <span className="muted" style={{ marginLeft: 6, fontSize: 12 }}>历史</span> : null}
                        </td>
                        <td className="muted">{r.customer_name || r.customer_code || '—'}</td>
                        <td className="muted">{r.customer_repair_number || '—'}</td>
                        <td style={{ textAlign: 'right' }}>{r.item_count ?? 0}</td>
                        <td style={{ textAlign: 'right' }}>{r.in_progress_count ?? 0}</td>
                        <td style={{ textAlign: 'right' }}>{r.qualified_count ?? 0}</td>
                        <td style={{ textAlign: 'right' }}>{r.delivered_count ?? 0}</td>
                        <td><Pill tone={s.tone} label={s.label} /></td>
                        <td><Pill tone={d.tone} label={d.label} /></td>
                        <td className="muted">{dateLabel(r.receipt_date || r.created_at)}</td>
                      </tr>
                      {open ? (
                        <tr className="detail-row">
                          <td colSpan={10}>
                            <ReworkBatchDetailPanel id={r.id} canWrite={canWrite} onChanged={() => void load()} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="pager">
          <span className="info">{loading ? '加载中…' : rows.length === 0 ? '无匹配记录' : `第 ${page * limit + 1}–${page * limit + rows.length} 条 / 共 ${total} 条`}</span>
          <div className="spacer" />
          <button className="btn" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || loading}>上一页</button>
          <button className="btn" onClick={() => setPage((p) => p + 1)} disabled={!hasMore || loading}>下一页</button>
        </div>
      </section>
    </>
  );
}
